// API client: attaches the Cognito access token and the org context headers, and turns every
// failure into an ApiError with the API's error code and the request id (x-request-id), which
// error toasts offer as "Copy reference".

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

export interface OrgContext {
  tenantId: string;
  orgUnitId: string;
}

let baseUrl = '';
let getToken: () => Promise<string | undefined> = () => Promise.resolve(undefined);

export function configureApi(opts: {
  baseUrl: string;
  getToken: () => Promise<string | undefined>;
}): void {
  baseUrl = opts.baseUrl.replace(/\/$/, '');
  getToken = opts.getToken;
}

export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown; org?: OrgContext | null } = {},
): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.org) {
    headers['x-tenant-id'] = opts.org.tenantId;
    headers['x-org-context'] = opts.org.orgUnitId;
  }

  let res: Response;
  try {
    const init: RequestInit = { method: opts.method ?? 'GET', headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    res = await fetch(`${baseUrl}${path}`, init);
  } catch {
    throw new ApiError(0, 'network', 'Network error');
  }

  const requestId = res.headers.get('x-request-id') ?? undefined;
  const data: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    const error = (data as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(
      res.status,
      error?.code ?? 'internal',
      error?.message ?? res.statusText,
      requestId,
    );
  }
  return data as T;
}

// ── API shapes ─────────────────────────────────────────────────────────────────────────────

export interface MfaStatus {
  required: boolean;
  reason: 'platform' | 'organization' | null;
  enrolled: boolean;
  enforceFrom: string | null;
  blocking: boolean;
}

export interface Membership {
  tenant: { id: string; name: string };
  orgUnit: { id: string; name: string; type: string };
  role: string;
  scope: 'node' | 'subtree';
}

export interface Me {
  user: { id: string; email: string; displayName: string };
  mfa: MfaStatus;
  session: { idleTimeoutMinutes: number };
  memberships: Membership[];
}

export type AccountState = 'active' | 'invited' | 'reset_required' | 'locked' | 'unknown';

export interface Person {
  id: string;
  email: string;
  displayName: string;
  mfaEnrolled: boolean;
  isPlatformStaff: boolean;
  memberships: { orgUnitId: string; orgUnitName: string; role: string; scope: string }[];
  account: { state: AccountState; failedSignIns: number };
}

export interface OrgUnit {
  id: string;
  parentId: string | null;
  type: string;
  name: string;
  isCustodian: boolean;
  province: string | null;
  status: string;
}
